<template>
  <div class="bg-page">
    <!-- 顶部概览：简洁主色渐变背景 -->
    <div class="bg-gradient-primary text-white p-4 shadow-sm mb-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-white bg-opacity-20 flex items-center justify-center backdrop-blur-sm rounded-full overflow-hidden" @click="$router.push('/mobile/profile')">
            <img v-if="userInfo.avatar" :src="userInfo.avatar" class="w-full h-full object-cover" />
            <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-bold">{{ userInfo.name || '用户' }}</h2>
            <p class="text-xs opacity-90">{{ greeting }}</p>
          </div>
        </div>
        <div class="text-right">
          <p class="text-lg font-bold">{{ currentDate }}</p>
        </div>
      </div>
    </div>

    <!-- 快捷操作 -->
    <div class="mobile-quick-grid home-quick-grid px-2">
      <button v-for="action in quickActions" :key="action.key" @click="handleQuickAction(action.key)" :class="['mobile-quick-action', `quick-action-${action.key}`]">
        <div class="mobile-quick-icon bg-primary-light relative">
          <div v-if="action.badge" class="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white z-10">
            {{ action.badge > 99 ? '99+' : action.badge }}
          </div>
          <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="action.icon === 'add-sale'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            <path v-else-if="action.icon === 'add-purchase'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            <path v-else-if="action.icon === 'product-management'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a2 2 0 012-2h5.5L20 11.5V19a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm7 0v6h6M8 16h8" />
            <path v-else-if="action.icon === 'inventory'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            <path v-else-if="action.icon === 'seal'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h8v8H8zM10 10V7.5a2 2 0 014 0V10M6 18h12M9 18v2.5h6V18M5 20.5h14" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <span class="mobile-quick-label">{{ action.label }}</span>
      </button>
    </div>
    <div class="px-2">
      <!-- 快速统计 -->
      <div class="mobile-stats-grid">
        <div v-for="stat in quickStats" :key="stat.key" @click="handleStatClick(stat.key)" class="mobile-stat-card">
          <p class="mobile-stat-label">{{ stat.label }}</p>
          <p class="mobile-stat-value">{{ stat.value }}</p>
          <p v-if="stat.growth !== undefined" class="mobile-stat-growth"
            :class="stat.growth >= 0 ? 'text-success' : 'text-error'">
            <i :class="stat.growth >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
            {{ Math.abs(stat.growth) }}%
          </p>
        </div>
      </div>
      <div class="mobile-chart-card mb-3">
        <div class="mobile-chart-header mb-3 flex justify-between items-center">
          <h3 class="mobile-chart-title">
            <i class="fas fa-clipboard-check icon-primary"></i>
            待办审批
          </h3>
          <div class="flex items-center" @click="$router.push('/mobile/approval')">
            <span class="text-xs text-gray-500 mr-1">查看全部</span>
            <span v-if="pendingApprovalCount > 0 || pendingApprovals.length > 0" class="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
              {{ pendingApprovalCount || pendingApprovals.length }}
            </span>
            <i class="fas fa-chevron-right text-xs text-gray-400 ml-1"></i>
          </div>
        </div>

        <div v-if="loadingApprovals" class="flex justify-center items-center h-[200px]">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
        <div v-else-if="pendingApprovals.length === 0" class="flex items-center justify-center h-[200px] text-gray-400 text-sm">
          暂无待办审批
        </div>
        <div v-else class="h-[200px] overflow-y-auto space-y-2 pr-1">
          <div v-for="item in pendingApprovals" :key="item.id" 
               :class="['bg-white rounded-lg p-3 active:bg-gray-50 border shadow-sm relative', getCardBorderClass(item.businessType)]"
               @click="openApprovalModal(item)">
            <!-- 关键信息第一行：类型、单号、金额 -->
            <div class="flex justify-between items-start mb-2">
              <div class="flex items-center gap-2 overflow-hidden">
                <span :class="['text-xs px-2 py-0.5 rounded flex items-center gap-1 whitespace-nowrap', getBusinessTypeClass(item.businessType)]">
                  <i :class="getBusinessIcon(item.businessType)"></i>
                  {{ item.businessType || '审批' }}
                </span>
                <span class="text-sm font-bold text-gray-900 truncate">
                  {{ item.businessNo || item.title || item.businessKey || '无单号' }}
                </span>
              </div>
              <div v-if="isPaymentApproval(item) && (item.orderAmount || item.totalAmount || item.amount)" class="flex flex-col items-end">
                <span class="font-bold text-red-600 whitespace-nowrap">
                  ¥{{ formatCurrency(item.orderAmount || item.amount) }}
                </span>
                <span class="text-xs text-gray-500 whitespace-nowrap">
                  实际付款: ¥{{ formatCurrency(item.totalAmount || item.orderAmount || item.amount) }}
                </span>
              </div>
              <span v-else-if="item.amount" class="font-bold text-red-600 whitespace-nowrap">
                ¥{{ formatNumber(item.amount) }}
              </span>
            </div>
            
            <!-- 内容网格 -->
            <div class="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-600 mb-2">
               <!-- 第一行：申请人 & 日期 -->
               <div class="flex items-center truncate">
                  <i class="fas fa-user mr-1.5 text-gray-400 w-3 text-center"></i>
                  <span class="truncate">{{ item.submitterName || item.proposer || '未知申请人' }}</span>
               </div>
               <div class="flex items-center justify-end truncate">
                  <i class="far fa-clock mr-1.5 text-gray-400"></i>
                  <span>{{ formatDateSimple(item.createdTime || item.startTime) }}</span>
               </div>

               <!-- 第二行：特定字段 -->
               <template v-if="item.businessType === '印章申请'">
                   <div class="col-span-2 flex items-center truncate">
                       <i class="fas fa-stamp mr-1.5 text-gray-400 w-3 text-center"></i>
                       <span class="text-gray-500 mr-1">类型:</span>
                       <span class="font-medium text-gray-800">{{ getSealTypeZh(item.sealType) }}</span>
                   </div>
               </template>
               
               <template v-else-if="item.businessType === '付款申请'">
                   <div class="col-span-2 flex items-center truncate">
                       <i class="fas fa-store mr-1.5 text-gray-400 w-3 text-center"></i>
                       <span class="text-gray-500 mr-1">供应商:</span>
                       <span class="font-medium text-gray-800 truncate">{{ item.supplierName || '-' }}</span>
                   </div>
               </template>
               
               <template v-else-if="item.businessType === '开票申请'">
                   <div class="col-span-2 flex items-center truncate">
                       <i class="fas fa-building mr-1.5 text-gray-400 w-3 text-center"></i>
                       <span class="text-gray-500 mr-1">客户:</span>
                       <span class="font-medium text-gray-800 truncate">{{ item.customerName || '-' }}</span>
                   </div>
                   <div class="col-span-2 flex items-center truncate">
                       <i class="fas fa-file-invoice mr-1.5 text-gray-400 w-3 text-center"></i>
                       <span class="text-gray-500 mr-1">类型:</span>
                       <span class="font-medium text-gray-800 truncate">{{ getInvoiceTypeZh(item.invoiceType) }}</span>
                   </div>
               </template>
            </div>

            <!-- 备注 (如果有) -->
            <div v-if="item.remarks || item.reason" class="text-xs text-gray-500 bg-gray-50 p-1.5 rounded truncate flex items-center">
              <i class="fas fa-comment-alt mr-1.5 text-gray-400 text-xs"></i>
              <span class="truncate">{{ item.remarks || item.reason }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 审批详情弹窗 -->
      <ApprovalDetailDrawer
        v-model:visible="showDrawer"
        :item="selectedItem"
        @refresh="checkAndPromptNext"
      />

      <!-- 资产情况卡片 -->
      <div class="mobile-chart-card">
        <div class="mobile-chart-header mb-3">
          <h3 class="mobile-chart-title">
            <i class="fas fa-coins icon-primary"></i>
            资产情况
          </h3>
          <div class="text-xs text-gray-500">
            {{ assetStatistics.statisticsTime }}
          </div>
        </div>

        <!-- 总资产概览 -->
        <div class="asset-overview mb-3">
          <div class="asset-total">
            <p class="asset-total-value">{{ (assetStatistics.netAssets.toFixed(2)) }}元</p>
            <p class="asset-formula text-xs">总资产 = 账户余额 + 库存总额 + 应收欠款 - 应付欠款</p>
          </div>
        </div>

        <!-- 资产详情 - 使用网格布局更紧凑 -->
        <div class="grid grid-cols-2 gap-2">
          <div class="asset-item-compact">
            <div class="flex items-center">
              <div class="asset-item-icon-small bg-blue-100 mr-2">
                <i class="fas fa-wallet text-blue-600 text-xs"></i>
              </div>
              <div class="flex-1">
                <p class="asset-item-label-small">账户余额</p>
                <p class="asset-item-value-small text-blue-600">{{ (assetStatistics.accountBalance.toFixed(2)) }}</p>
              </div>
            </div>
          </div>

          <div class="asset-item-compact">
            <div class="flex items-center">
              <div class="asset-item-icon-small bg-green-100 mr-2">
                <i class="fas fa-boxes text-green-600 text-xs"></i>
              </div>
              <div class="flex-1">
                <p class="asset-item-label-small">库存总额</p>
                <p class="asset-item-value-small text-green-600">{{ (assetStatistics.inventoryAmount.toFixed(2)) }}</p>
              </div>
            </div>
          </div>

          <div class="asset-item-compact">
            <div class="flex items-center">
              <div class="asset-item-icon-small bg-orange-100 mr-2">
                <i class="fas fa-hand-holding-usd text-orange-600 text-xs"></i>
              </div>
              <div class="flex-1">
                <p class="asset-item-label-small">应收欠款</p>
                <p class="asset-item-value-small text-orange-600">{{ (assetStatistics.receivableAmount.toFixed(2)) }}
                </p>
              </div>
            </div>
          </div>

          <div class="asset-item-compact">
            <div class="flex items-center">
              <div class="asset-item-icon-small bg-red-100 mr-2">
                <i class="fas fa-credit-card text-red-600 text-xs"></i>
              </div>
              <div class="flex-1">
                <p class="asset-item-label-small">应付欠款</p>
                <p class="asset-item-value-small text-red-600">{{ (assetStatistics.payableAmount.toFixed(2)) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 图表区域 -->
      <div class="mobile-chart-card">
        <div class="mobile-chart-header">
          <h3 class="mobile-chart-title">
            <i class="fas fa-chart-line icon-primary"></i>
            销售和进货趋势
          </h3>
          <!-- <div class="relative">
          <select 
            v-model="chartPeriod" 
            class="mobile-chart-select pr-8"
            @change="loadChartData"
          >
            <option value="7">近7天</option>
            <option value="15">近15天</option>
            <option value="30">近30天</option>
          </select>
         
        </div> -->
        </div>

        <!-- ECharts 图表容器 -->
        <div class="mobile-chart-container relative">
          <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span class="ml-2 text-gray-500">加载中...</span>
          </div>
          <div ref="salesTrendChart" class="w-full h-64"></div>
        </div>


        <!-- 统计 -->
        <div class="mobile-chart-stats">
          <div class="mobile-chart-stat">
            <p class="mobile-chart-stat-label">销售最高</p>
            <p class="mobile-chart-stat-value text-blue-600">¥{{formatNumber(Math.max(...chartData.map(item =>
              item.sales))) }}</p>
          </div>
          <div class="mobile-chart-stat">
            <p class="mobile-chart-stat-label">销售平均</p>
            <p class="mobile-chart-stat-value text-green-600">¥{{formatNumber(chartData.length ? chartData.reduce((sum,
              item) => sum + item.sales, 0) / chartData.length : 0) }}</p>
          </div>
          <div class="mobile-chart-stat">
            <p class="mobile-chart-stat-label">进货最高</p>
            <p class="mobile-chart-stat-value text-blue-600">¥{{formatNumber(Math.max(...chartData.map(item =>
              item.purchase))) }}</p>
          </div>
          <div class="mobile-chart-stat">
            <p class="mobile-chart-stat-label">进货平均</p>
            <p class="mobile-chart-stat-value text-green-600">¥{{formatNumber(chartData.length ? chartData.reduce((sum,
              item) => sum + item.purchase, 0) / chartData.length : 0) }}</p>
          </div>
        </div>
      </div>

      <!-- 月度销售图表 -->
      <div class="mobile-chart-card mt-3">
        <div class="mobile-chart-header flex flex-col space-y-2">
          <div class="flex justify-between items-center w-full">
            <h3 class="mobile-chart-title">
              <i class="fas fa-chart-bar icon-primary"></i>
              月度销售趋势
            </h3>
            <button 
              @click="generateMonthlyRecords" 
              :disabled="generatingMonthlyData"
              class="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 flex items-center transition-colors"
            >
              <i class="fas fa-sync-alt mr-1" :class="{'fa-spin': generatingMonthlyData}"></i>
              {{ generatingMonthlyData ? '刷新中' : '刷新' }}
            </button>
          </div>
          
          <!-- 业务员下拉列表 -->
          <div class="w-full salesman-selector relative">
            <div class="relative">
              <input 
                type="text" 
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs h-8 focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8 cursor-pointer bg-gray-50"
                :value="getSelectedSalesmanNames()"
                @click.stop="toggleSalesmanDropdown"
                placeholder="点击选择业务员 (默认全部)"
                readonly
              />
              <i class="fas fa-chevron-down absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none text-xs"></i>
              
              <div v-if="showSalesmanDropdown" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" @click.stop>
                <div class="p-2">
                  <div class="flex items-center space-x-2 mb-2">
                    <button 
                      @click="selectAllSalesmen"
                      class="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      全选
                    </button>
                    <button 
                      @click="clearAllSalesmen"
                      class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                    >
                      清空
                    </button>
                  </div>
                  <div v-for="s in salesmanList" :key="s.id" class="flex items-center space-x-2 py-2 border-b border-gray-50 last:border-0 active:bg-gray-50">
                    <input 
                      type="checkbox" 
                      :id="`salesman-${s.id}`"
                      :value="s.id"
                      v-model="selectedSalesmen"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label :for="`salesman-${s.id}`" class="text-xs cursor-pointer flex-1 text-gray-700">
                      {{ s.name }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mobile-chart-container relative">
          <div v-if="loadingMonthly" class="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span class="ml-2 text-gray-500">加载中...</span>
          </div>
          <div ref="monthlySalesChart" class="w-full h-64" style="min-height: 250px;"></div>
        </div>
        <div v-if="monthlyDataError" class="text-xs text-center text-gray-400 py-2">
          {{ monthlyDataError }}
        </div>
      </div>

    </div>
    <ConfirmDialog
      v-model:visible="showNextApprovalDialog"
      title="操作成功"
      message="还有待办审批，是否继续审批下一条？"
      confirmText="继续审批"
      @confirm="handleNextApprovalConfirm"
    />
    <ConfirmDialog
      v-model:visible="showQuickApproveDialog"
      title="确认审批"
      message="确认通过该审批？"
      confirmText="确认通过"
      @confirm="confirmQuickApprove"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { dashboardApi, approvalApi, inventoryApi, salesmanApi } from '@/api/api'
import * as echarts from 'echarts'
import { toast } from '@/utils/toast.js'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ApprovalDetailDrawer from '@/mobile/components/ApprovalDetailDrawer.vue'
import configManager from '@/utils/configManager';
const loadConfig = async () => {
  try {
    configData.value = await configManager.getConfig();
    console.log('银行账户页面配置加载成功:', configData.value);
  } catch (error) {
    console.warn('加载配置数据失败，使用默认配置:', error);
    configData.value = configManager.getDefaultConfig();
  }
};
const configData = ref({
  settings: { enableAdjustments: false },
  adjustmentValues: { bankAccount: { totalBalance: 0, totalIncome: 0, totalAccountCount: 0, netInflow: 0 } }
});
export default {
  name: 'HomePage',
  components: {
    ConfirmDialog,
    ApprovalDetailDrawer
  },
  setup() {
    const router = useRouter()

    const userInfo = ref({
      name: '管理员',
      role: '系统管理员'
    })

    const chartPeriod = ref('7')
    const chartData = ref([])
    const selectedChart = ref(-1)
    const loading = ref(false)
    const salesTrendChart = ref(null)
    let salesTrendChartInstance = null

    // 月度销售图表相关
    const loadingMonthly = ref(false)
    const monthlySalesChart = ref(null)
    let monthlySalesChartInstance = null
    const monthlyDataError = ref('')
    const generatingMonthlyData = ref(false)

    const generateMonthlyRecords = async () => {
      try {
        generatingMonthlyData.value = true
        monthlyDataError.value = ''
        const response = await dashboardApi.syncMonthlyRecords(new Date().getFullYear())
        if (response.success) {
          toast.success('数据已刷新')
          await loadMonthlySalesData()
        } else {
          toast.error(response.message || '刷新失败')
          monthlyDataError.value = response.message || '刷新失败'
        }
      } catch (error) {
        console.error('刷新月度数据失败:', error)
        toast.error('刷新失败')
        monthlyDataError.value = '刷新请求失败'
      } finally {
        generatingMonthlyData.value = false
      }
    }

    // 资产统计相关数据
    const assetLoading = ref(false)
    const assetPieChart = ref(null)
    let assetPieChartInstance = null
    const assetStatistics = ref({
      accountBalance: 0,
      inventoryAmount: 0,
      receivableAmount: 0,
      payableAmount: 0,
      totalAssets: 0,
      netAssets: 0,
      statisticsTime: ''
    })

    const pendingApprovalCount = ref(0)
    const pendingApprovals = ref([])
    const loadingApprovals = ref(false)
    const showNextApprovalDialog = ref(false)
    
    // 弹窗相关状态
    const showDrawer = ref(false)
    const selectedItem = ref({}) 

    const currentDate = computed(() => {
      const now = new Date()
      return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
    })

    const greeting = computed(() => {
      const hour = new Date().getHours()
      if (hour < 9) return '早上好'
      if (hour < 12) return '上午好'
      if (hour < 14) return '中午好'
      if (hour < 18) return '下午好'
      return '晚上好'
    })

    const formatNumber = (num) => {
      if (num === undefined || num === null) return '0'
      const n = Number(num)
      if (n >= 10000) {
        return (n / 10000).toFixed(1) + '万'
      }
      return n.toLocaleString()
    }

    const formatCurrency = (num) => {
      if (num === undefined || num === null) return '0.00'
      return Number(num).toFixed(2)
    }

    const isPaymentApproval = (item) => {
      const type = item?.businessType || item?.type || ''
      if (type === '付款申请' || type === '付款申请(小额)' || type === 'payment') return true
      return String(type).toLowerCase() === 'payment'
    }

    // 业务员筛选相关
    const salesmanList = ref([])
    const selectedSalesmen = ref([])
    const showSalesmanDropdown = ref(false)

    const getSalesmanList = async () => {
      try {
        const response = await salesmanApi.getAllSalemans()
        if (response.success) {
          salesmanList.value = response.data || []
        }
      } catch (error) {
        console.error('获取业务员列表失败:', error)
      }
    }

    const getSelectedSalesmanNames = () => {
      if (selectedSalesmen.value.length === 0) return '全部业务员'
      const names = salesmanList.value
        .filter(s => selectedSalesmen.value.includes(s.id))
        .map(s => s.name)
      if (names.length === 0) return '全部业务员'
      if (names.length <= 2) return names.join(', ')
      return `${names[0]}, ${names[1]} 等${names.length}人`
    }

    const toggleSalesmanDropdown = () => {
      showSalesmanDropdown.value = !showSalesmanDropdown.value
    }

    const selectAllSalesmen = () => {
      selectedSalesmen.value = salesmanList.value.map(s => s.id)
    }

    const clearAllSalesmen = () => {
      selectedSalesmen.value = []
    }

    const handleClickOutside = (event) => {
      const container = event.target.closest('.salesman-selector')
      if (!container && showSalesmanDropdown.value) {
        showSalesmanDropdown.value = false
      }
    }

    watch(selectedSalesmen, () => {
      loadMonthlySalesData()
    })

    const loadMonthlySalesData = async () => {
      try {
        loadingMonthly.value = true
        monthlyDataError.value = ''
        
        if (!monthlySalesChartInstance && monthlySalesChart.value) {
          monthlySalesChartInstance = echarts.init(monthlySalesChart.value)
          // 绑定 resize
          const resizeHandler = () => {
            if (monthlySalesChartInstance) monthlySalesChartInstance.resize()
          }
          window.addEventListener('resize', resizeHandler)
          // 保存 handler 以便清理
          if (monthlySalesChart.value) {
              monthlySalesChart.value._resizeHandler = resizeHandler
          }
        }
        
        if (monthlySalesChartInstance) {
          monthlySalesChartInstance.showLoading()
        }

        // 获取所有业务员的数据
        console.log('正在请求月度销售数据...')
        const response = await dashboardApi.getSalesTrend12Months(selectedSalesmen.value)
        console.log('月度销售数据响应:', response)
        
        if (response.success || response.isSuccess) { // 兼容 isSuccess
          const data = response.data || response.result
          console.log('月度销售数据内容:', data)
          if (data && data.labels && data.datasets) {
            updateMonthlySalesChart(data)
          } else {
            console.warn('月度销售数据格式不正确', data)
            monthlyDataError.value = '数据格式错误'
          }
        } else {
          console.error('月度销售数据请求失败', response)
          monthlyDataError.value = response.message || '加载失败'
        }
      } catch (error) {
        console.error('加载月度销售数据失败:', error)
        monthlyDataError.value = '网络请求失败'
      } finally {
        loadingMonthly.value = false
        if (monthlySalesChartInstance) {
          monthlySalesChartInstance.hideLoading()
        }
      }
    }

    const updateMonthlySalesChart = (data) => {
      if (!monthlySalesChartInstance) return

      // 过滤掉未知业务员的数据
      let filteredDatasets = data.datasets.filter(d => d.label !== '未知业务员')
      
      // 如果过滤后为空，但原始数据不为空，则显示原始数据（防止误过滤）
      if (filteredDatasets.length === 0 && data.datasets.length > 0) {
         console.warn('过滤后数据集为空，使用原始数据集')
         filteredDatasets = data.datasets
      }

      if (filteredDatasets.length === 0) {
        monthlyDataError.value = '暂无数据'
        return
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          confine: true, // 限制在图表区域内
          axisPointer: {
            type: 'cross',
            label: { backgroundColor: '#6a7985' }
          }
        },
        legend: {
          data: filteredDatasets.map(d => d.label),
          bottom: 0,
          type: 'scroll', // 移动端启用滚动图例
          pageButtonPosition: 'start'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%', // 增加底部间距给图例
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            boundaryGap: false,
            data: data.labels,
            axisLabel: {
              interval: 'auto', // 自动间隔
              rotate: 0 // 不旋转
            }
          }
        ],
        yAxis: [
          { type: 'value' }
        ],
        series: filteredDatasets.map(d => ({
          name: d.label,
          type: 'line',
          smooth: true,
          areaStyle: d.fill ? {
            opacity: 0.1,
            color: d.backgroundColor || d.borderColor // 确保有颜色
          } : null,
          emphasis: { focus: 'series' },
          data: d.data,
          itemStyle: { color: d.borderColor },
          lineStyle: {
            width: 2,
            type: d.borderDash ? 'dashed' : 'solid'
          }
        }))
      }
      
      monthlySalesChartInstance.setOption(option, true)
      monthlySalesChartInstance.resize() // 强制调整大小
    }

    const handleApplicationAction = (type) => {
      const routes = {
        payment: '/mobile/approval/payment/add',
        invoice: '/mobile/approval/invoice/add',
        seal: '/mobile/approval/seal/add'
      }
      router.push(routes[type])
    }

    const quickActions = [
      { key: 'add-sale', label: '开销售单', icon: 'add-sale' },
      { key: 'add-purchase', label: '开进货单', icon: 'add-purchase' },
      { key: 'product-management', label: '商品管理', icon: 'product-management' },
      { key: 'inventory', label: '库存查询', icon: 'inventory' },
      { key: 'sales-report', label: '销售报表', icon: 'report' },
      { key: 'seal-application', label: '印章申请', icon: 'seal' }
    ]

    // 快捷操作列表
    const handleQuickAction = (key) => {
      switch (key) {
        case 'add-sale': router.push('/mobile/sales'); break; 
        case 'add-purchase': router.push('/mobile/purchase'); break;
        case 'product-management': router.push('/mobile/product-management'); break;
        case 'inventory': router.push('/mobile/inventory'); break;
        case 'approval': router.push('/mobile/approval'); break;
        case 'sales-report': router.push('/mobile/home/sales-report'); break;
        case 'seal-application': router.push('/mobile/approval/seal/add'); break;
      }
    }

    const quickStats = ref([
      { key: 'sales', label: '今日销售', value: '0', growth: 0 },
      { key: 'purchase', label: '今日进货', value: '0', growth: 0 },
      { key: 'inventory', label: '库存预警', value: '0' },
      { key: 'profit', label: '今日毛利', value: '0', growth: 0 }
    ])

    const maxChartValue = computed(() => {
       if (!chartData.value.length) return 0
       const maxSales = Math.max(...chartData.value.map(i => i.sales || 0))
       const maxPurchase = Math.max(...chartData.value.map(i => i.purchase || 0))
       return Math.max(maxSales, maxPurchase)
    })

    const loadChartData = async () => {
      loading.value = true
      try {
        const endDate = new Date()
        const startDate = new Date()
        startDate.setDate(endDate.getDate() - parseInt(chartPeriod.value))
        
        const startDateStr = formatLocalDate(startDate)
        const endDateStr = formatLocalDate(endDate)

        const response = await dashboardApi.getSalesReport({
          startDate: startDateStr,
          endDate: endDateStr
        })

        if (response.success || response.isSuccess) {
          const data = response.data?.chartData?.salesTrend || []
          
          chartData.value = data.map(item => {
            let displayDate = item.date
            if (item.date && item.date.includes('-')) {
              const dateParts = item.date.split('-')
              if (dateParts.length === 2) {
                displayDate = `${parseInt(dateParts[0])}/${parseInt(dateParts[1])}`
              } else if (dateParts.length >= 3) {
                displayDate = `${parseInt(dateParts[1])}/${parseInt(dateParts[2])}`
              }
            }
            return {
              date: displayDate,
              sales: Number(item.sales) || 0,
              purchase: Number(item.purchase) || 0
            }
          })
        
          if (salesTrendChartInstance) {
             const option = {
               tooltip: {
                 trigger: 'axis',
                 axisPointer: { type: 'cross' },
                 formatter: function (params) {
                   let result = params[0].name + '<br/>'
                   params.forEach(param => {
                     result += param.marker + param.seriesName + ': ¥' + formatNumber(param.value) + '<br/>'
                   })
                   return result
                 }
               },
               grid: {
                 left: '3%',
                 right: '4%',
                 bottom: '10%',
                 top: '10%',
                 containLabel: true
               },
               xAxis: {
                 type: 'category',
                 boundaryGap: false,
                 data: chartData.value.map(i => i.date),
                 axisLabel: { fontSize: 10 }
               },
               yAxis: {
                 type: 'value',
                 axisLabel: {
                   formatter: function (value) {
                     if (value >= 10000) return '¥' + (value / 10000).toFixed(1) + '万'
                     return '¥' + value
                   },
                   fontSize: 10
                 }
               },
               series: [
                 {
                   name: '销售',
                   type: 'line',
                   smooth: true,
                   data: chartData.value.map(i => i.sales),
                   itemStyle: { color: '#3B82F6' },
                   lineStyle: { width: 2 },
                   areaStyle: {
                     color: {
                       type: 'linear',
                       x: 0, y: 0, x2: 0, y2: 1,
                       colorStops: [
                         { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
                         { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
                       ]
                     }
                   }
                 },
                 {
                   name: '进货',
                   type: 'line',
                   smooth: true,
                   data: chartData.value.map(i => i.purchase),
                   itemStyle: { color: '#10B981' },
                   lineStyle: { width: 2 },
                   areaStyle: {
                     color: {
                       type: 'linear',
                       x: 0, y: 0, x2: 0, y2: 1,
                       colorStops: [
                         { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
                         { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
                       ]
                     }
                   }
                 }
               ]
             }
             salesTrendChartInstance.setOption(option, true)
          }
        }
      } catch (e) { 
        console.error('加载图表数据失败:', e) 
      } finally { 
        loading.value = false 
      }
    }

    const handleChartPeriodChange = () => loadChartData()
    const showChartDetail = () => {}

    const getSealTypeZh = (type) => {
      if (!type) return '-'
      const map = {
        'official': '公章',
        'company': '公司公章',
        'contract': '合同专用章',
        'finance': '财务专用章',
        'invoice': '发票章',
        'legal': '法人章',
        'other': '其他'
      }
      return map[type.toLowerCase()] || type
    }

    const getInvoiceTypeZh = (type) => {
      const mapStr = {
        'special': '增值税专用发票',
        'vat_special': '增值税专用发票',
        'normal': '增值税普通发票',
        'electronic': '电子发票',
        'electronic_normal': '电子普通发票'
      }
      const mapNum = { '1': '增值税专用发票', '2': '增值税普通发票', '3': '电子发票' }
      const s = String(type).trim().toLowerCase()
      return mapStr[s] || mapNum[s] || (type ? type : '-')
    }

    const getPaymentMethodZh = (method) => {
      const map = {
        'bank_transfer': '银行转账',
        'cash': '现金',
        'check': '支票',
        'wechat': '微信支付',
        'alipay': '支付宝'
      }
      return map[(method || '').toLowerCase()] || method || '-'
    }

    const formatTaxRate = (rate) => {
      if (rate == null) return '-'
      const n = Number(rate)
      if (isNaN(n)) return String(rate)
      return `${(n * (n < 1 ? 100 : 1)).toFixed(2)}%`
    }

    const getUrgencyText = (level) => {
      const map = {
        'normal': '普通',
        'urgent': '紧急',
        'very_urgent': '特急',
        '1': '普通',
        '2': '紧急',
        '3': '特急'
      }
      return map[level] || '普通'
    }

    const getBusinessTypeClass = (type) => {
      switch(type) {
        case '付款申请': return 'bg-green-100 text-green-700'
        case '开票申请': return 'bg-blue-100 text-blue-700'
        case '印章申请': return 'bg-purple-100 text-purple-700'
        default: return 'bg-gray-100 text-gray-700'
      }
    }

    const getBusinessIcon = (type) => {
      switch(type) {
        case '付款申请': return 'fas fa-money-bill-wave'
        case '开票申请': return 'fas fa-file-invoice-dollar'
        case '印章申请': return 'fas fa-stamp'
        default: return 'fas fa-file-alt'
      }
    }
    
    const getCardBorderClass = (type) => {
      switch(type) {
        case '付款申请': return 'border-green-500'
        case '开票申请': return 'border-blue-500'
        case '印章申请': return 'border-purple-500'
        default: return 'border-gray-300'
      }
    }

    const initCharts = () => {
      if (!salesTrendChart.value) return
      salesTrendChartInstance = echarts.init(salesTrendChart.value)
      salesTrendChartInstance.setOption({
        tooltip: { trigger: 'axis' },
        grid: { top: 10, right: 10, bottom: 20, left: 10, containLabel: true },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [
            { name: '销售', type: 'line', smooth: true, data: [], itemStyle: { color: '#3b82f6' } },
            { name: '进货', type: 'line', smooth: true, data: [], itemStyle: { color: '#10b981' } }
        ]
      })
      loadChartData()
    }

    const initAssetPieChart = () => {}

    const formatLocalDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const calculateGrowthRate = (current, previous) => {
      if (!previous || previous === 0) return current > 0 ? 100 : 0
      return Math.round(((current - previous) / previous) * 100)
    }

    const loadTodayData = async () => {
      try {
        const today = formatLocalDate(new Date())

        // 获取今日销售数据
        const todayResponse = await dashboardApi.getSalesReport({
          startDate: today,
          endDate: today
        })
        if (todayResponse.success || todayResponse.isSuccess) {
          const todayData = todayResponse.data?.summary || todayResponse.result?.summary || {}
          quickStats.value[0].value = formatNumber(todayData.totalSales || 0)
          quickStats.value[3].value = formatNumber(todayData.grossProfit || 0)
        }

        // 获取今日进货数据
        const todayPurchaseResponse = await dashboardApi.getPurchaseReport({
          startDate: today,
          endDate: today
        })
        if (todayPurchaseResponse.success || todayPurchaseResponse.isSuccess) {
          const todayPurchaseData = todayPurchaseResponse.data?.summary || todayPurchaseResponse.result?.summary || {}
          quickStats.value[1].value = formatNumber(todayPurchaseData.totalPurchase || 0)
        }
      } catch (error) {
        console.error('加载今日数据失败:', error)
      }
    }

    const loadMonthData = async () => {
      try {
        const now = new Date()
        const thisMonthStart = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1))
        const thisMonthEnd = formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))

        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const lastMonthStart = formatLocalDate(lastMonth)
        const lastMonthEnd = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 0))

        // 1. 销售增长率
        const [thisMonthSales, lastMonthSales] = await Promise.all([
          dashboardApi.getSalesReport({ startDate: thisMonthStart, endDate: thisMonthEnd }),
          dashboardApi.getSalesReport({ startDate: lastMonthStart, endDate: lastMonthEnd })
        ])

        if ((thisMonthSales.success || thisMonthSales.isSuccess) && (lastMonthSales.success || lastMonthSales.isSuccess)) {
          const thisMonth = thisMonthSales.data?.summary?.totalSales || 0
          const lastMonth = lastMonthSales.data?.summary?.totalSales || 0
          quickStats.value[0].growth = calculateGrowthRate(thisMonth, lastMonth)
          
          // 毛利增长率
          const thisMonthProfit = thisMonthSales.data?.summary?.grossProfit || 0
          const lastMonthProfit = lastMonthSales.data?.summary?.grossProfit || 0
          quickStats.value[3].growth = calculateGrowthRate(thisMonthProfit, lastMonthProfit)
        }

        // 2. 进货增长率
        const [thisMonthPurchase, lastMonthPurchase] = await Promise.all([
          dashboardApi.getPurchaseReport({ startDate: thisMonthStart, endDate: thisMonthEnd }),
          dashboardApi.getPurchaseReport({ startDate: lastMonthStart, endDate: lastMonthEnd })
        ])

        if ((thisMonthPurchase.success || thisMonthPurchase.isSuccess) && (lastMonthPurchase.success || lastMonthPurchase.isSuccess)) {
          const thisMonth = thisMonthPurchase.data?.summary?.totalPurchase || 0
          const lastMonth = lastMonthPurchase.data?.summary?.totalPurchase || 0
          quickStats.value[1].growth = calculateGrowthRate(thisMonth, lastMonth)
        }

      } catch (error) {
        console.error('加载月度对比数据失败:', error)
      }
    }

    const loadInventoryStats = async () => {
      try {
        const response = await inventoryApi.getInventoryStatistics()
        if (response.success || response.isSuccess) {
          const data = response.data || response.result || {}
          quickStats.value[2].value = data.warningCount || 0
        }
      } catch (error) {
        console.error('加载库存统计失败:', error)
      }
    }
    const loadAssetStatistics = async () => {
        try {
            const res = await dashboardApi.getAssetStatistics({ merchantId: userInfo.value.merchantId })
            if (res.success || res.succeeded) {
                assetStatistics.value = res.data || assetStatistics.value
            }
        } catch (e) {
          console.error(e)
        }
    }
    const loadApprovalStats = async () => {
        try {
            const res = await dashboardApi.getPendingApprovalStats()
            if (res.success || res.succeeded) {
                const count = res.data.totalCount || 0
                pendingApprovalCount.value = count
                const action = quickActions.find(a => a.key === 'approval')
                if (action) action.badge = count
            }
        } catch (e) {
          console.error(e)
        }
    }

    const formatDateSimple = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}-${date.getDate()}`
    }

    const loadPendingApprovals = async () => {
      loadingApprovals.value = true
      try {
        // 增加pageSize以获取更多数据，或者按需加载
        const res = await approvalApi.getPendingApprovals({ page: 1, pageSize: 20, status: 'pending' })
        if (res.success || res.succeeded) {
           const data = res.data || res
           let list = []
           if (Array.isArray(data)) {
             list = data
           } else {
             list = data.list || data.items || []
           }
           pendingApprovals.value = list.map(item => {
             if (!isPaymentApproval(item)) return item
             const rawJson = item.jsonData || item.JsonData || item.businessData || item.data
             let parsed = rawJson || {}
             if (typeof rawJson === 'string') {
               try {
                 parsed = JSON.parse(rawJson)
               } catch (e) {
                 parsed = {}
               }
             }
             const payment = parsed.payment || {}
             const orderAmount = payment.amount ?? parsed.amount ?? item.amount
             const totalAmount = payment.totalAmount ?? parsed.totalAmount ?? orderAmount
             return {
               ...item,
               orderAmount,
               totalAmount
             }
           })
           
           // 如果没有单独获取统计，可以用列表长度作为兜底
           if (pendingApprovalCount.value === 0 && pendingApprovals.value.length > 0) {
             pendingApprovalCount.value = pendingApprovals.value.length
           }
        }
      } catch (e) { console.error(e) }
      finally { loadingApprovals.value = false }
    }

    // 打开审批弹窗
    const openApprovalModal = (item) => {
      selectedItem.value = item
      showDrawer.value = true
    }

    // 关闭弹窗
    const closeModal = () => {
      showDrawer.value = false
      selectedItem.value = {}
    }

    const checkAndPromptNext = async () => {
      closeModal()
      await loadPendingApprovals()
      await loadApprovalStats()
      
      if (pendingApprovals.value.length > 0) {
        setTimeout(() => {
          showNextApprovalDialog.value = true
        }, 300)
      } else {
        toast.success('所有待办已审批完成')
      }
    }

    const handleNextApprovalConfirm = async () => {
      showNextApprovalDialog.value = false
      if (pendingApprovals.value.length > 0) {
        const nextItem = pendingApprovals.value[0]
        await openApprovalModal(nextItem)
      }
    }
    
    const showQuickApproveDialog = ref(false)
    const quickApproveItem = ref(null)

    const handleQuickApprove = (item) => {
      quickApproveItem.value = item
      showQuickApproveDialog.value = true
    }

    const confirmQuickApprove = async () => {
      showQuickApproveDialog.value = false
      if (!quickApproveItem.value) return
      
      const item = quickApproveItem.value
      const id = item.id || item.instanceId
      try {
        const res = await approvalApi.approveInstance(id, { remarks: '快速审批通过' })
        if (res.success || res.succeeded) {
          toast.success('已通过')
          loadPendingApprovals()
          loadApprovalStats()
        } else {
          toast.error('操作失败: ' + (res.message || '未知错误'))
        }
      } catch (e) {
        console.error(e)
        toast.error('操作失败')
      }
    }

    const handleQuickReject = async (item) => {
      const reason = prompt('请输入驳回原因：')
      if (reason === null) return
      if (!reason.trim()) {
        alert('请输入驳回原因')
        return
      }
      const id = item.id || item.instanceId
      try {
        const res = await approvalApi.rejectInstance(id, { remarks: reason })
        if (res.success || res.succeeded) {
          alert('已驳回')
          loadPendingApprovals()
          loadApprovalStats()
        } else {
          alert('操作失败: ' + (res.message || '未知错误'))
        }
      } catch (e) {
        console.error(e)
        alert('操作失败')
      }
    }

    const goToApprovalDetail = (item) => {
      console.log('Navigate to detail', item);
      
      const typeMap = {
        '付款申请': 'payment',
        '付款申请(小额)': 'payment',
        'payment': 'payment',
        '开票申请': 'invoice',
        'Invoice': 'invoice',
        'invoice': 'invoice',
        '公章申请': 'seal',
        '公章申请(合同)': 'seal',
        'seal': 'seal'
      };
      
      // Try to match businessType or type, handling case sensitivity if needed (though map keys are explicit)
      const typeKey = item.businessType || item.type || '';
      const type = typeMap[typeKey] || typeMap[Object.keys(typeMap).find(k => k.toLowerCase() === typeKey.toLowerCase())];
      
      // Ensure we have an ID (check id, instanceId, businessKey)
      const id = item.id || item.instanceId || item.businessKey;
      
      if (type && id) {
        router.push(`/mobile/approval/${type}/${id}`);
      } else {
        console.error('Unknown approval type or missing ID:', item);
        // If we can't identify the type, maybe just go to the list
        router.push('/mobile/approval');
      }
    };

    const handleStatClick = (key) => {
      const routes = {
        'sales': '/mobile/sales',
        'purchase': '/mobile/purchase',
        'inventory': '/mobile/inventory',
        'profit': '/mobile/finance'
      }
      if (routes[key]) router.push(routes[key])
    }

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside)
      // 加载用户信息
      const savedUserInfo = localStorage.getItem('userInfo')
      if (savedUserInfo) userInfo.value = JSON.parse(savedUserInfo)
      await loadConfig(); // 先加载配置
      // 初始化图表
      await nextTick()
      initCharts()
      initAssetPieChart()
      // 初始化月度销售图表
      if (monthlySalesChart.value) {
        monthlySalesChartInstance = echarts.init(monthlySalesChart.value)
      }

      await getSalesmanList()

      // 并行加载所有数据
      await Promise.all([
        loadTodayData(),
        loadMonthData(),
        loadInventoryStats(),
        loadAssetStatistics(),
        loadApprovalStats(),
        loadPendingApprovals(),
        loadMonthlySalesData()
      ])
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      // 清理图表实例
      if (salesTrendChartInstance) {
        salesTrendChartInstance.dispose()
        salesTrendChartInstance = null
      }

      if (assetPieChartInstance) {
        assetPieChartInstance.dispose()
        assetPieChartInstance = null
      }

      if (monthlySalesChartInstance) {
        monthlySalesChartInstance.dispose()
        monthlySalesChartInstance = null
      }

      // 清理resize事件监听器
      if (salesTrendChart.value && salesTrendChart.value._resizeHandler) {
        window.removeEventListener('resize', salesTrendChart.value._resizeHandler)
      }

      if (assetPieChart.value && assetPieChart.value._resizeHandler) {
        window.removeEventListener('resize', assetPieChart.value._resizeHandler)
      }

      if (monthlySalesChart.value && monthlySalesChart.value._resizeHandler) {
        window.removeEventListener('resize', monthlySalesChart.value._resizeHandler)
      }
    })

    return {
      userInfo,
      currentDate,
      greeting,
      quickStats,
      quickActions,
      chartPeriod,
      chartData,
      selectedChart,
      maxChartValue,
      loading,
      salesTrendChart,
      // 业务员筛选
      salesmanList,
      selectedSalesmen,
      showSalesmanDropdown,
      getSelectedSalesmanNames,
      toggleSalesmanDropdown,
      selectAllSalesmen,
      clearAllSalesmen,
      // 月度销售相关
      loadingMonthly,
      monthlySalesChart,
      generatingMonthlyData,
      generateMonthlyRecords,
      monthlyDataError,
      formatNumber,
      formatCurrency,
      isPaymentApproval,
      loadChartData,
      handleChartPeriodChange,
      handleQuickAction,
      handleApplicationAction,
      handleStatClick,
      showChartDetail,
      // 资产相关
      assetLoading,
      assetPieChart,
      assetStatistics,
      pendingApprovalCount,
      pendingApprovals,
      loadingApprovals,
      formatDateSimple,
      handleQuickApprove,
      handleQuickReject,
      goToApprovalDetail,
      showDrawer,
      selectedItem,
      openApprovalModal,
      closeModal,
      checkAndPromptNext,
      getBusinessTypeClass,
      getBusinessIcon,
      getCardBorderClass,
      getSealTypeZh,
      getInvoiceTypeZh,
      getPaymentMethodZh,
      formatTaxRate,
      getUrgencyText,
      showNextApprovalDialog,
      handleNextApprovalConfirm,
      showQuickApproveDialog,
      confirmQuickApprove
    }
  }
}
</script>

<style scoped>
.home-quick-grid {
  width: min(100%, 560px);
  margin-left: auto;
  margin-right: auto;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  justify-content: center;
}

.home-quick-grid .quick-action-sales-report {
  grid-column: 2;
}

.home-quick-grid .quick-action-seal-application {
  grid-column: 3;
}

@media (max-width: 360px) {
  .home-quick-grid {
    gap: 2px;
  }

  .home-quick-grid .mobile-quick-action {
    padding-left: 2px;
    padding-right: 2px;
  }
}


.active\:scale-95:active {
  transform: scale(0.95);
}

.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}

/* Chart styles */
.mobile-chart-bar-container {
  @apply relative flex items-end justify-center flex-1 h-20 cursor-pointer;
}

.mobile-chart-bar {
  @apply w-2 transition-all duration-300 hover:opacity-80;
  min-height: 4px;
}

.mobile-chart-stats {
  @apply grid grid-cols-4 gap-2 pt-2 border-t;
  border-color: var(--border-light);
}

.mobile-chart-stat {
  @apply text-center;
}

.mobile-chart-stat-label {
  @apply text-xs;
  color: var(--text-tertiary);
}

.mobile-chart-stat-value {
  @apply text-sm font-semibold;
}

.mobile-stat-growth {
  @apply text-xs font-medium mt-1;
}

/* tooltip */
.border-t-gray-800 {
  border-top-color: #1f2937;
}

/* 资产情况卡片样式 */
.asset-overview {
  @apply mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg;
}

.asset-total {
  @apply text-center;
}

.asset-total-label {
  @apply text-sm font-medium text-gray-600 mb-1;
}

.asset-total-value {
  @apply text-2xl font-bold text-gray-900 mb-2;
}

.asset-formula {
  @apply text-xs text-gray-500;
}

.asset-details {
  @apply space-y-3 mb-4;
}

.asset-item {
  @apply flex items-center p-3 bg-white rounded-lg shadow-sm border border-gray-100;
}

.asset-item-icon {
  @apply w-10 h-10 rounded-full flex items-center justify-center mr-3;
}

.asset-item-content {
  @apply flex-1;
}

.asset-item-label {
  @apply text-sm font-medium text-gray-600;
}

.asset-item-value {
  @apply text-lg font-bold;
}

.net-asset {
  @apply mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200;
}

.net-asset-content {
  @apply text-center;
}

.net-asset-label {
  @apply text-sm font-medium text-gray-600 mb-1;
}

.net-asset-value {
  @apply text-xl font-bold;
}

/* 紧凑型资产卡片样式 */
.asset-item-compact {
  @apply bg-white rounded-md p-2 shadow-sm border border-gray-100;
}

.asset-item-icon-small {
  @apply w-6 h-6 rounded flex items-center justify-center;
}

.asset-item-label-small {
  @apply text-xs text-gray-500 leading-4;
}

.asset-item-value-small {
  @apply text-sm font-semibold leading-5;
}

.asset-item-label {
  @apply text-sm font-medium text-gray-600;
}

.asset-item-value {
  @apply text-lg font-bold;
}

.net-asset {
  @apply mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200;
}

.net-asset-content {
  @apply text-center;
}

.net-asset-label {
  @apply text-sm font-medium text-gray-600 mb-1;
}

.net-asset-value {
  @apply text-xl font-bold;
}
</style>





















